import { Autocomplete, Box, TextField } from "@mui/material";
import React, { useState } from "react";
import DeletableChip from "./DeletableChip";

interface MultiSelectDropdownProps {
  label: string;
  value: string[];
  options: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  getChipLabel?: (value: string) => string;
  getOptionLabel?: (value: string) => string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  searchable = false,
  getChipLabel,
  getOptionLabel,
}) => {
  const [inputValue, setInputValue] = useState("");
  const handleChange = (event: any, newValue: string[]) => {
    onChange(newValue || []);
  };

  // Filter options based on input value - search both value and label
  const filteredOptions =
    searchable && inputValue
      ? options.filter((option) => {
          const optionValue = option.toLowerCase();
          const optionLabel = getOptionLabel
            ? getOptionLabel(option).toLowerCase()
            : optionValue;
          const searchTerm = inputValue.toLowerCase();
          return (
            optionValue.includes(searchTerm) || optionLabel.includes(searchTerm)
          );
        })
      : options;

  return (
    <Autocomplete
      multiple
      options={filteredOptions}
      value={value}
      onChange={handleChange}
      inputValue={searchable ? inputValue : ""}
      onInputChange={
        searchable
          ? (event, newInputValue) => setInputValue(newInputValue)
          : undefined
      }
      getOptionLabel={(option) =>
        getOptionLabel ? getOptionLabel(option) : option
      }
      isOptionEqualToValue={(option, value) => option === value}
      filterOptions={(options) => options} // We handle filtering manually
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : ""}
          variant="outlined"
          size="small"
          InputLabelProps={{
            ...params.InputLabelProps,
            sx: {
              color: "#e0e0e0",
              "&.MuiInputLabel-shrink": {
                transform: "translate(14px, -9px) scale(0.75)",
              },
            },
            shrink: value.length > 0 || (searchable && inputValue.length > 0),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#1e1e1e",
              "& fieldset": {
                borderColor: "#555",
              },
              "&:hover fieldset": {
                borderColor: "#777",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#1976d2",
              },
            },
            "& .MuiInputBase-input": {
              color: "#e0e0e0",
            },
            "& .MuiInputBase-input::placeholder": {
              color: "#888888",
              opacity: 1,
            },
          }}
        />
      )}
      renderTags={(tagValue, getTagProps) => (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            paddingTop: "2px",
          }}
        >
          {tagValue.map((option, index) => {
            const tagProps = getTagProps({ index });
            return (
              <DeletableChip
                key={option}
                label={getChipLabel ? getChipLabel(option) : option}
                onDelete={() => {
                  if (tagProps.onDelete) {
                    tagProps.onDelete({} as any);
                  }
                }}
              />
            );
          })}
        </Box>
      )}
      sx={{
        "& .MuiAutocomplete-listbox": {
          maxHeight: "300px",
          "& .MuiAutocomplete-option": {
            padding: "8px 16px",
            '&[aria-selected="true"]': {
              backgroundColor: "#1976d2 !important",
            },
            "&.Mui-focused": {
              backgroundColor: "#333333 !important",
            },
          },
        },
        "& .MuiAutocomplete-popper": {
          "& .MuiPaper-root": {
            backgroundColor: "#262626",
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
    />
  );
};

export default MultiSelectDropdown;
